import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  SbModal,
  SbTabs,
  SbTable,
  SbButton,
  SbNotification,
  SbDropdown,
  SbSelect,
  SbTag,
  SbInput,
  DROPDOWN_MODE,
  EllipsisOutlined,
  TeamOutlined,
  DeleteOutlined,
  WarningFilled,
  SearchOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from "uiCore";
import { ShareAltOutlined } from "@ant-design/icons";
import { updatePAICArchiveStatus, delegatePAICProject, sharePAICProject, transferPAICProjectOwner } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import {
  PAIC_ARCHIVE_STATUS,
  PAIC_PROJECT_SOURCE,
  MANAGE_GROUPS_TABS,
  handlePAICErrorCode,
} from "constants/peoplePlusAI.constants";
import { DOMAIN_KEY } from "constants/localStorage.constants";
import {
  ModalTitleRow,
  ModalTitleIcon,
  ModalSearchRow,
  ModalSearchIconBox,
  TabLabelWithCount,
  TabCount,
  RoleBadge,
  GroupNameCell,
  ActionsCell,
  ActionMenuButton,
  ModalFooterRow,
  FooterSummary,
  ManageGroupsTableWrapper,
  DeleteModalTitleRow,
  DeleteModalTitleIcon,
  DeleteGroupNameBox,
  DeleteWarningBox,
  DeleteWarningText,
  ConfirmLabel,
  ConfirmInputWrapper,
  ModalActionFooter,
  TransferModalTitleRow,
  TransferModalTitleIcon,
  TransferDescription,
  TransferWarningBox,
  TransferWarningText,
  TransferFieldLabel,
  TransferHelperText,
  DelegateModalTitleRow,
  DelegateModalTitleIcon,
  DelegateDescription,
  DelegateInfoBox,
  DelegateInfoText,
  DelegateFieldLabel,
  DelegateInputWrapper,
  DelegateHelperText,
  ShareFieldStack,
  ShareSectionDivider,
  SharedUsersPanel,
  SharedUsersHeader,
  SharedUsersHeaderContent,
  SharedUsersCount,
  SharedUsersSearchWrapper,
  SharedUsersList,
  SharedUsersResultMeta,
  SharedUserItem,
  SharedUserName,
  SharedUserEmail,
  SharedUsersEmptyText,
  SharedUsersNoResultsText,
} from "./ManageGroupsModal.styles";

const normalizeEmailList = (values = []) =>
  Array.from(
    new Set(
      values
        .flatMap((value) => String(value || "").split(/[;,]+/))
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean)
    )
  );

const formatFailures = (failures = []) =>
  failures
    .map((failure) => {
      if (typeof failure === "string") return failure;
      return `${failure.email || failure.user || "User"}: ${failure.reason || failure.message || "Validation failed"}`;
    })
    .join(", ");

const getFailureEmail = (failure) => {
  if (!failure) return "";
  if (typeof failure === "string") return failure.trim().toLowerCase();
  return String(failure.email || failure.user || "").trim().toLowerCase();
};

const getEmailSet = (items = []) =>
  new Set(items.map(getFailureEmail).filter(Boolean));

const getSuccessfulRequestedCount = (requestedEmails = [], failed = [], skipped = []) => {
  const failedEmails = getEmailSet(failed);
  const skippedEmails = getEmailSet(skipped);

  return requestedEmails.filter((email) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    return normalizedEmail && !failedEmails.has(normalizedEmail) && !skippedEmails.has(normalizedEmail);
  }).length;
};

const hasResponseCount = (response = {}, keys = []) =>
  keys.some((key) => response[key] !== undefined && response[key] !== null);

const getShareSummary = (response = {}) => {
  const sharedCountKeys = ["shared_count", "added_count"];
  const removedCountKeys = [
    "removed_count",
    "removed_shared_count",
    "unshared_count",
    "removed",
  ];
  const sharedCount = Number(response.shared_count || response.added_count || 0);
  const removedCount = Number(
    response.removed_count ||
    response.removed_shared_count ||
    response.unshared_count ||
    response.removed ||
    0
  );
  const failed = Array.isArray(response.failed) ? response.failed : [];
  const skipped = Array.isArray(response.skipped)
    ? response.skipped
    : Array.isArray(response.already_shared)
      ? response.already_shared
      : [];

  return {
    sharedCount,
    removedCount,
    failed,
    skipped,
    hasSharedCount: hasResponseCount(response, sharedCountKeys),
    hasRemovedCount: hasResponseCount(response, removedCountKeys),
  };
};

const getDelegateSummary = (response = {}) => {
  const delegatedCountKeys = ["delegated_count", "added_count"];
  const removedCountKeys = [
    "removed_count",
    "removed_delegated_count",
    "undelegated_count",
    "removed",
  ];
  const delegatedCount = Number(response.delegated_count || response.added_count || 0);
  const removedCount = Number(
    response.removed_count ||
    response.removed_delegated_count ||
    response.undelegated_count ||
    response.removed ||
    0
  );
  const failed = Array.isArray(response.failed) ? response.failed : [];
  const skipped = Array.isArray(response.skipped)
    ? response.skipped
    : Array.isArray(response.already_delegated)
      ? response.already_delegated
      : [];

  return {
    delegatedCount,
    removedCount,
    failed,
    skipped,
    hasDelegatedCount: hasResponseCount(response, delegatedCountKeys),
    hasRemovedCount: hasResponseCount(response, removedCountKeys),
  };
};

const getUserEmail = (user = {}) => {
  if (typeof user === "string") return user;
  return user.email || user.user || "";
};

const getDisplayName = (user = {}) => {
  if (typeof user === "string") return user;
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ").trim();
  return name || getUserEmail(user) || "User";
};

const getProjectDelegateUsers = (project = {}) =>
  project.delegate_users || project.delegated_users || project.delegates || [];

const getProjectSharedUsers = (project = {}) =>
  project.shared_users || project.shares || [];

const getAccessUserCount = (users = []) => (Array.isArray(users) ? users.length : 0);

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeDomain = (domain = "") => {
  const normalized = String(domain || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/^www\./, "")
    .replace(/^@/, "")
    .split("/")[0];

  return normalized.includes("@") ? normalized.split("@").pop() : normalized;
};

const getEmailDomain = (email = "") => {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  if (!EMAIL_REGEX.test(normalizedEmail)) return "";
  return normalizeDomain(normalizedEmail.split("@").pop());
};

const getUniqueDomains = (domains = []) =>
  Array.from(
    new Set(
      (Array.isArray(domains) ? domains : [domains])
        .map((domain) => normalizeDomain(domain))
        .filter(Boolean)
    )
  );

const toDomainArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return value.split(/[,;]+/);
  return [];
};

const getMetadataOrganizationDomains = (orgMetaData = {}) =>
  getUniqueDomains([
    ...toDomainArray(orgMetaData?.org_domain),
    ...toDomainArray(orgMetaData?.org_domains),
    ...toDomainArray(orgMetaData?.org_metadata_json?.org_domain),
    ...toDomainArray(orgMetaData?.org_metadata_json?.org_domains),
  ]);

const getOrganizationEmailDomains = (currentUserEmail = "", orgMetaData = {}, orgDomains = []) => {
  const apiDomains = getUniqueDomains(orgDomains);

  if (apiDomains.length > 0) return apiDomains;

  const metadataDomains = getMetadataOrganizationDomains(orgMetaData);

  if (metadataDomains.length > 0) return metadataDomains;

  const storedDomain = normalizeDomain(localStorage.getItem(DOMAIN_KEY));
  const fallbackDomain = getEmailDomain(currentUserEmail);

  return getUniqueDomains(storedDomain || fallbackDomain);
};

const formatOrganizationEmailHint = (domains = []) => {
  const allowedDomains = getUniqueDomains(domains);

  if (allowedDomains.length === 0) return "your organization email domain";

  const formattedDomains = allowedDomains.map((domain) => `@${domain}`);

  if (formattedDomains.length === 1) return formattedDomains[0];
  if (formattedDomains.length === 2) return `${formattedDomains[0]} or ${formattedDomains[1]}`;

  return `${formattedDomains.slice(0, -1).join(", ")}, or ${formattedDomains[formattedDomains.length - 1]}`;
};

const getInvalidOrganizationEmails = (emails = [], organizationDomains = []) => {
  const allowedDomains = getUniqueDomains(organizationDomains);

  return normalizeEmailList(emails).filter((email) => {
    const emailDomain = getEmailDomain(email);
    if (!emailDomain) return true;
    if (allowedDomains.length === 0) return false;
    return !allowedDomains.includes(emailDomain);
  });
};

const validateOrganizationEmails = (emails = [], organizationDomains = [], accessLabel = "access") => {
  const invalidEmails = getInvalidOrganizationEmails(emails, organizationDomains);

  if (invalidEmails.length === 0) return true;

  const organizationEmailHint = formatOrganizationEmailHint(organizationDomains);
  const domainMessage = organizationEmailHint !== "your organization email domain"
    ? `${accessLabel} is limited to users with ${organizationEmailHint} email addresses.`
    : `${accessLabel} is limited to users with valid organization email addresses.`;

  SbNotification.warning(
    "Use organization email only",
    `${domainMessage} Please remove or replace: ${invalidEmails.join(", ")}`
  );

  return false;
};

const filterOrganizationEmails = (emails = [], organizationDomains = [], accessLabel = "access") => {
  const normalizedEmails = normalizeEmailList(emails);
  const invalidEmails = getInvalidOrganizationEmails(normalizedEmails, organizationDomains);

  if (invalidEmails.length > 0) {
    validateOrganizationEmails(invalidEmails, organizationDomains, accessLabel);
  }

  return normalizedEmails.filter((email) => !invalidEmails.includes(email));
};

const renderAccessStatusTag = (count = 0, label = "access") => {
  const hasAccess = count > 0;

  return (
    <SbTag
      color={hasAccess ? "green" : "default"}
      title={hasAccess ? `${count} ${label} user${count === 1 ? "" : "s"}` : `No ${label} users`}
    >
      {hasAccess ? "Yes" : "No"}
    </SbTag>
  );
};

const BADGE_LABELS = {
  [PAIC_PROJECT_SOURCE.OWNED]: "Owner",
  [PAIC_PROJECT_SOURCE.DELEGATED]: "Co-creator",
  [PAIC_PROJECT_SOURCE.SHARED]: "Shared",
};

const ManageGroupsModal = ({
  open,
  onClose,
  ownedProjects,
  delegatedProjects,
  sharedProjects = [],
  onRefresh,
  onSelectGroup,
}) => {
  const currentUserEmail = useAuthStore((state) => state.email);
  const orgDomains = useAuthStore((state) => state.orgDomains);
  const orgMetaData = useAuthStore((state) => state.orgMetaData);
  const [activeTab, setActiveTab] = useState(MANAGE_GROUPS_TABS.MY_GROUPS);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const refreshTimeoutRef = useRef(null);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteProject, setDeleteProject] = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // Delegate modal state
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [delegateProject, setDelegateProject] = useState(null);
  const [delegateEmailsToAdd, setDelegateEmailsToAdd] = useState([]);
  const [delegateEmailsToRemove, setDelegateEmailsToRemove] = useState([]);
  const [delegateAddSearchValue, setDelegateAddSearchValue] = useState("");
  const [delegateUserSearchTerm, setDelegateUserSearchTerm] = useState("");
  const [delegateLoading, setDelegateLoading] = useState(false);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareProject, setShareProject] = useState(null);
  const [shareEmailsToAdd, setShareEmailsToAdd] = useState([]);
  const [shareEmailsToRemove, setShareEmailsToRemove] = useState([]);
  const [shareAddSearchValue, setShareAddSearchValue] = useState("");
  const [shareUserSearchTerm, setShareUserSearchTerm] = useState("");
  const [shareLoading, setShareLoading] = useState(false);

  // Transfer ownership modal state
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [transferProject, setTransferProject] = useState(null);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  // Search filtering
  const normalizeSearch = (str) => (str || "").toLowerCase().trim();

  const filteredOwnedProjects = useMemo(() => {
    if (!modalSearchTerm.trim()) return ownedProjects || [];
    const term = normalizeSearch(modalSearchTerm);
    return (ownedProjects || []).filter(
      (p) =>
        normalizeSearch(p.project_name).includes(term) ||
        normalizeSearch(p.sb_project_number).includes(term) ||
        normalizeSearch(p.status).includes(term)
    );
  }, [ownedProjects, modalSearchTerm]);

  const filteredDelegatedProjects = useMemo(() => {
    if (!modalSearchTerm.trim()) return delegatedProjects || [];
    const term = normalizeSearch(modalSearchTerm);
    return (delegatedProjects || []).filter(
      (p) =>
        normalizeSearch(p.project_name).includes(term) ||
        normalizeSearch(p.sb_project_number).includes(term) ||
        normalizeSearch(p.status).includes(term)
    );
  }, [delegatedProjects, modalSearchTerm]);

  const filteredSharedProjects = useMemo(() => {
    if (!modalSearchTerm.trim()) return sharedProjects || [];
    const term = normalizeSearch(modalSearchTerm);
    return (sharedProjects || []).filter(
      (p) =>
        normalizeSearch(p.project_name).includes(term) ||
        normalizeSearch(p.sb_project_number).includes(term) ||
        normalizeSearch(p.status).includes(term)
    );
  }, [sharedProjects, modalSearchTerm]);

  const ownedCount = filteredOwnedProjects?.length || 0;
  const delegatedCount = filteredDelegatedProjects?.length || 0;
  const sharedCount = filteredSharedProjects?.length || 0;
  const totalCount = (ownedProjects?.length || 0) + (delegatedProjects?.length || 0) + (sharedProjects?.length || 0);

  const latestProjectsById = useMemo(() => {
    const projectMap = new Map();
    [...(ownedProjects || []), ...(delegatedProjects || []), ...(sharedProjects || [])].forEach((project) => {
      if (project?.sb_project_unique_id) {
        projectMap.set(project.sb_project_unique_id, project);
      }
    });
    return projectMap;
  }, [ownedProjects, delegatedProjects, sharedProjects]);

  const syncProjectSnapshot = useCallback(
    (project, closeModal) => {
      if (!project?.sb_project_unique_id) return project;

      const latestProject = latestProjectsById.get(project.sb_project_unique_id);

      if (!latestProject) {
        closeModal?.();
        return null;
      }

      return latestProject;
    },
    [latestProjectsById]
  );

  const existingDelegateUsers = delegateProject?.delegate_users || delegateProject?.delegated_users || delegateProject?.delegates || [];
  const existingDelegateEmailSet = useMemo(
    () => new Set(existingDelegateUsers.map((user) => getUserEmail(user).toLowerCase()).filter(Boolean)),
    [existingDelegateUsers]
  );
  const existingDelegateUserOptions = useMemo(
    () => existingDelegateUsers
      .filter((user) => getUserEmail(user))
      .map((user) => {
        const email = getUserEmail(user).toLowerCase();
        return {
          value: email,
          label: `${getDisplayName(user)} (${email})`,
        };
      }),
    [existingDelegateUsers]
  );
  const filteredDelegateUsers = useMemo(() => {
    const term = normalizeSearch(delegateUserSearchTerm);
    if (!term) return existingDelegateUsers;

    return existingDelegateUsers.filter((user) =>
      normalizeSearch(`${getDisplayName(user)} ${getUserEmail(user)}`).includes(term)
    );
  }, [existingDelegateUsers, delegateUserSearchTerm]);
  const existingSharedUsers = shareProject?.shared_users || [];
  const existingSharedEmailSet = useMemo(
    () => new Set(existingSharedUsers.map((user) => getUserEmail(user).toLowerCase()).filter(Boolean)),
    [existingSharedUsers]
  );
  const existingSharedUserOptions = useMemo(
    () => existingSharedUsers
      .filter((user) => getUserEmail(user))
      .map((user) => {
        const email = getUserEmail(user).toLowerCase();
        return {
          value: email,
          label: `${getDisplayName(user)} (${email})`,
        };
      }),
    [existingSharedUsers]
  );
  const filteredSharedUsers = useMemo(() => {
    const term = normalizeSearch(shareUserSearchTerm);
    if (!term) return existingSharedUsers;

    return existingSharedUsers.filter((user) =>
      normalizeSearch(`${getDisplayName(user)} ${getUserEmail(user)}`).includes(term)
    );
  }, [existingSharedUsers, shareUserSearchTerm]);

  useEffect(() => () => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }
  }, []);

  const refreshAfterProjectOperation = useCallback(async () => {
    if (!onRefresh) return null;

    const refreshedProjects = await onRefresh();

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      Promise.resolve(onRefresh())
        .catch((refreshError) => {
          console.warn("Unable to complete follow-up PAIC groups refresh:", refreshError);
        })
        .finally(() => {
          refreshTimeoutRef.current = null;
        });
    }, 700);

    return refreshedProjects;
  }, [onRefresh]);

  const delegateEmailsToSubmit = useMemo(
    () => normalizeEmailList([...delegateEmailsToAdd, delegateAddSearchValue]),
    [delegateEmailsToAdd, delegateAddSearchValue]
  );

  const shareEmailsToSubmit = useMemo(
    () => normalizeEmailList([...shareEmailsToAdd, shareAddSearchValue]),
    [shareEmailsToAdd, shareAddSearchValue]
  );

  const organizationEmailDomains = useMemo(
    () => getOrganizationEmailDomains(currentUserEmail, orgMetaData, orgDomains),
    [currentUserEmail, orgMetaData, orgDomains]
  );

  const organizationEmailHint = useMemo(
    () => formatOrganizationEmailHint(organizationEmailDomains),
    [organizationEmailDomains]
  );

  const hasInvalidDelegateEmails = useMemo(
    () => getInvalidOrganizationEmails(delegateEmailsToSubmit, organizationEmailDomains).length > 0,
    [delegateEmailsToSubmit, organizationEmailDomains]
  );

  const hasInvalidShareEmails = useMemo(
    () => getInvalidOrganizationEmails(shareEmailsToSubmit, organizationEmailDomains).length > 0,
    [shareEmailsToSubmit, organizationEmailDomains]
  );

  const isTransferEmailInvalid = useMemo(
    () => Boolean(transferEmail.trim()) && getInvalidOrganizationEmails([transferEmail], organizationEmailDomains).length > 0,
    [transferEmail, organizationEmailDomains]
  );

  const isDelegateSubmitDisabled =
    (delegateEmailsToSubmit.length === 0 && delegateEmailsToRemove.length === 0) ||
    delegateLoading ||
    hasInvalidDelegateEmails;

  const isShareSubmitDisabled =
    (shareEmailsToSubmit.length === 0 && shareEmailsToRemove.length === 0) ||
    shareLoading ||
    hasInvalidShareEmails;

  const isTransferSubmitDisabled = !transferEmail.trim() || transferLoading || isTransferEmailInvalid;

  // ── Delete Group ──
  const isDeleteConfirmMatch =
    deleteProject && deleteConfirmName.trim() === deleteProject.project_name;

  const openDeleteModal = useCallback((project) => {
    setDeleteProject(project);
    setDeleteConfirmName("");
    setDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setDeleteProject(null);
    setDeleteConfirmName("");
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteProject || !isDeleteConfirmMatch) return;
    setActionLoading(true);
    try {
      const response = await updatePAICArchiveStatus(
        deleteProject.sb_project_unique_id,
        PAIC_ARCHIVE_STATUS.ARCHIVED
      );
      SbNotification.success(
        "Group Archived",
        response?.message || `"${deleteProject.project_name}" has been archived.`
      );
      closeDeleteModal();
      await refreshAfterProjectOperation();
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error("Archive Failed", "Unable to archive the group. Please try again.");
      }
    } finally {
      setActionLoading(false);
    }
  }, [deleteProject, isDeleteConfirmMatch, refreshAfterProjectOperation, closeDeleteModal]);

  // ── Restore ──
  const handleRestore = useCallback(
    async (project) => {
      setActionLoading(true);
      try {
        const response = await updatePAICArchiveStatus(
          project.sb_project_unique_id,
          PAIC_ARCHIVE_STATUS.ACTIVE
        );
        SbNotification.success(
          "Group Restored",
          response?.message || `"${project.project_name}" has been restored.`
        );
        await refreshAfterProjectOperation();
      } catch (error) {
        if (!handlePAICErrorCode(error, SbNotification.error)) {
          SbNotification.error("Restore Failed", "Unable to restore the group. Please try again.");
        }
      } finally {
        setActionLoading(false);
      }
    },
    [refreshAfterProjectOperation]
  );

  // ── Delegate Access ──
  const openDelegateModal = useCallback((project) => {
    setDelegateProject(project);
    setDelegateEmailsToAdd([]);
    setDelegateEmailsToRemove([]);
    setDelegateAddSearchValue("");
    setDelegateUserSearchTerm("");
    setDelegateModalOpen(true);
  }, []);

  const closeDelegateModal = useCallback(() => {
    setDelegateModalOpen(false);
    setDelegateEmailsToAdd([]);
    setDelegateEmailsToRemove([]);
    setDelegateAddSearchValue("");
    setDelegateUserSearchTerm("");
    setDelegateProject(null);
  }, []);

  const handleDelegateSubmit = useCallback(async () => {
    if (!delegateProject || (delegateEmailsToSubmit.length === 0 && delegateEmailsToRemove.length === 0)) return;

    if (
      delegateEmailsToSubmit.length > 0 &&
      !validateOrganizationEmails(delegateEmailsToSubmit, organizationEmailDomains, "Delegate")
    ) {
      return;
    }

    const conflictingEmails = delegateEmailsToSubmit.filter((email) =>
      delegateEmailsToRemove.includes(email)
    );

    if (conflictingEmails.length > 0) {
      SbNotification.warning(
        "Resolve Delegate Conflicts",
        `These emails are in both add and remove lists: ${conflictingEmails.join(", ")}`
      );
      return;
    }

    const alreadyDelegatedEmails = delegateEmailsToSubmit.filter((email) =>
      existingDelegateEmailSet.has(email)
    );

    if (alreadyDelegatedEmails.length > 0) {
      SbNotification.warning(
        "Already Delegated",
        `These users already have co-creator access: ${alreadyDelegatedEmails.join(", ")}`
      );
      return;
    }

    setDelegateLoading(true);
    try {
      const response = await delegatePAICProject(
        delegateProject.sb_project_unique_id,
        delegateEmailsToSubmit,
        delegateEmailsToRemove
      );
      const {
        delegatedCount: addedCount,
        removedCount,
        failed,
        skipped,
        hasDelegatedCount,
        hasRemovedCount,
      } = getDelegateSummary(response);
      const effectiveAddedCount = hasDelegatedCount
        ? addedCount
        : getSuccessfulRequestedCount(delegateEmailsToSubmit, failed, skipped);
      const effectiveRemovedCount = hasRemovedCount
        ? removedCount
        : getSuccessfulRequestedCount(delegateEmailsToRemove, failed, skipped);
      const successDetails = [
        effectiveAddedCount > 0 ? `Added ${effectiveAddedCount} user(s)` : "",
        effectiveRemovedCount > 0 ? `Removed ${effectiveRemovedCount} user(s)` : "",
      ].filter(Boolean);

      if (successDetails.length > 0) {
        SbNotification.success(
          "Delegate Access Updated",
          response?.message || `${successDetails.join(", ")} successfully.`
        );
      }
      if (skipped.length > 0) {
        SbNotification.warning("Some Users Skipped", formatFailures(skipped));
      }
      if (failed.length > 0) {
        SbNotification.warning("Some Delegate Updates Failed", formatFailures(failed));
      }
      if (successDetails.length === 0 && failed.length === 0 && skipped.length === 0) {
        SbNotification.info(
          "No Delegate Changes Applied",
          "No delegated users were added or removed. The selected users may already be up to date."
        );
      }
      closeDelegateModal();
      await refreshAfterProjectOperation();
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error("Delegation Failed", "Unable to delegate the group. Please try again.");
      }
    } finally {
      setDelegateLoading(false);
    }
  }, [delegateProject, delegateEmailsToSubmit, delegateEmailsToRemove, organizationEmailDomains, existingDelegateEmailSet, refreshAfterProjectOperation, closeDelegateModal]);

  const handleDelegateEmailChange = useCallback((values) => {
    setDelegateEmailsToAdd(filterOrganizationEmails(values, organizationEmailDomains, "Delegate access"));
    setDelegateAddSearchValue("");
  }, [organizationEmailDomains]);

  const handleDelegateRemoveEmailChange = useCallback((values) => {
    setDelegateEmailsToRemove(normalizeEmailList(values));
  }, []);

  const handleDelegateInputKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      const inputValue = e.target?.value?.trim();
      if (!inputValue) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, []);

  // ── Transfer Ownership ──
  const openTransferModal = useCallback((project) => {
    setTransferProject(project);
    setTransferEmail("");
    setTransferModalOpen(true);
  }, []);

  const closeTransferModal = useCallback(() => {
    setTransferModalOpen(false);
    setTransferEmail("");
    setTransferProject(null);
  }, []);

  const handleTransferSubmit = useCallback(async () => {
    if (!transferProject || !transferEmail.trim()) return;

    const normalizedTransferEmail = transferEmail.trim().toLowerCase();

    if (!validateOrganizationEmails([normalizedTransferEmail], organizationEmailDomains, "Transfer")) {
      return;
    }

    setTransferLoading(true);
    try {
      const response = await transferPAICProjectOwner(
        transferProject.sb_project_unique_id,
        normalizedTransferEmail
      );
      SbNotification.success(
        "Ownership Transferred",
        response?.message || `"${transferProject.project_name}" has been transferred to ${normalizedTransferEmail}.`
      );
      closeTransferModal();
      await refreshAfterProjectOperation();
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(
          "Transfer Failed",
          "Unable to transfer ownership. Please try again."
        );
      }
    } finally {
      setTransferLoading(false);
    }
  }, [transferProject, transferEmail, organizationEmailDomains, closeTransferModal, refreshAfterProjectOperation]);

  // ── Share Access ──
  const openShareModal = useCallback((project) => {
    setShareProject(project);
    setShareEmailsToAdd([]);
    setShareEmailsToRemove([]);
    setShareAddSearchValue("");
    setShareUserSearchTerm("");
    setShareModalOpen(true);
  }, []);

  const closeShareModal = useCallback(() => {
    setShareModalOpen(false);
    setShareEmailsToAdd([]);
    setShareEmailsToRemove([]);
    setShareAddSearchValue("");
    setShareUserSearchTerm("");
    setShareProject(null);
  }, []);

  useEffect(() => {
    if (!delegateProject) return;
    setDelegateProject((currentProject) => syncProjectSnapshot(currentProject, closeDelegateModal));
  }, [delegateProject, syncProjectSnapshot, closeDelegateModal]);

  useEffect(() => {
    if (!shareProject) return;
    setShareProject((currentProject) => syncProjectSnapshot(currentProject, closeShareModal));
  }, [shareProject, syncProjectSnapshot, closeShareModal]);

  useEffect(() => {
    if (!transferProject) return;
    setTransferProject((currentProject) => syncProjectSnapshot(currentProject, closeTransferModal));
  }, [transferProject, syncProjectSnapshot, closeTransferModal]);

  useEffect(() => {
    if (!deleteProject) return;
    setDeleteProject((currentProject) => syncProjectSnapshot(currentProject, closeDeleteModal));
  }, [deleteProject, syncProjectSnapshot, closeDeleteModal]);

  const handleShareSubmit = useCallback(async () => {
    if (!shareProject || (shareEmailsToSubmit.length === 0 && shareEmailsToRemove.length === 0)) return;

    if (
      shareEmailsToSubmit.length > 0 &&
      !validateOrganizationEmails(shareEmailsToSubmit, organizationEmailDomains, "Share")
    ) {
      return;
    }

    const conflictingEmails = shareEmailsToSubmit.filter((email) =>
      shareEmailsToRemove.includes(email)
    );

    if (conflictingEmails.length > 0) {
      SbNotification.warning(
        "Resolve Share Conflicts",
        `These emails are in both add and remove lists: ${conflictingEmails.join(", ")}`
      );
      return;
    }

    const alreadySharedEmails = shareEmailsToSubmit.filter((email) =>
      existingSharedEmailSet.has(email)
    );

    if (alreadySharedEmails.length > 0) {
      SbNotification.warning(
        "Already Shared",
        `These users already have view access: ${alreadySharedEmails.join(", ")}`
      );
      return;
    }

    setShareLoading(true);
    try {
      const response = await sharePAICProject(
        shareProject.sb_project_unique_id,
        shareEmailsToSubmit,
        shareEmailsToRemove
      );
      const {
        sharedCount,
        removedCount,
        failed,
        skipped,
        hasSharedCount,
        hasRemovedCount,
      } = getShareSummary(response);
      const effectiveSharedCount = hasSharedCount
        ? sharedCount
        : getSuccessfulRequestedCount(shareEmailsToSubmit, failed, skipped);
      const effectiveRemovedCount = hasRemovedCount
        ? removedCount
        : getSuccessfulRequestedCount(shareEmailsToRemove, failed, skipped);
      const successDetails = [
        effectiveSharedCount > 0 ? `Added ${effectiveSharedCount} user(s)` : "",
        effectiveRemovedCount > 0 ? `Removed ${effectiveRemovedCount} user(s)` : "",
      ].filter(Boolean);

      if (successDetails.length > 0) {
        SbNotification.success(
          "Share Access Updated",
          `${successDetails.join(", ")} successfully.`
        );
      }
      if (skipped.length > 0) {
        SbNotification.warning("Some Users Skipped", formatFailures(skipped));
      }
      if (failed.length > 0) {
        SbNotification.warning("Some Share Updates Failed", formatFailures(failed));
      }
      if (successDetails.length === 0 && failed.length === 0 && skipped.length === 0) {
        SbNotification.info(
          "No Share Changes Applied",
          "No shared users were added or removed. The selected users may already be up to date."
        );
      }
      closeShareModal();
      await refreshAfterProjectOperation();
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error("Share Failed", "Unable to share the group. Please try again.");
      }
    } finally {
      setShareLoading(false);
    }
  }, [shareProject, shareEmailsToSubmit, shareEmailsToRemove, organizationEmailDomains, existingSharedEmailSet, refreshAfterProjectOperation, closeShareModal]);

  const handleShareEmailChange = useCallback((values) => {
    setShareEmailsToAdd(filterOrganizationEmails(values, organizationEmailDomains, "Share access"));
    setShareAddSearchValue("");
  }, [organizationEmailDomains]);

  const handleShareRemoveEmailChange = useCallback((values) => {
    setShareEmailsToRemove(normalizeEmailList(values));
  }, []);

  const handleShareInputKeyDown = useCallback((e) => {
    if (e.key === "Enter") {
      const inputValue = e.target?.value?.trim();
      if (!inputValue) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }, []);

  // ── Select Group ──
  const handleGroupClick = useCallback(
    (project) => {
      if (onSelectGroup) onSelectGroup(project);
      onClose();
    },
    [onSelectGroup, onClose]
  );

  // ── Action Menu ──
  const getActionMenuItems = useCallback(
    (project, source) => {
      if (source === PAIC_PROJECT_SOURCE.OWNED) {
        const isArchived = project.archived_status === PAIC_ARCHIVE_STATUS.ARCHIVED;
        return [
          { key: "select", label: "Select Group" },
          { key: "delegate", label: "Delegate Access" },
          { key: "share", label: "Share Access" },
          { key: "transfer", label: "Transfer Ownership" },
          { key: isArchived ? "restore" : "archive", label: isArchived ? "Restore" : "Archive" },
        ];
      }
      if (source === PAIC_PROJECT_SOURCE.SHARED) {
        return [{ key: "select", label: "Select Group" }];
      }
      return [{ key: "select", label: "Select Group" }];
    },
    []
  );

  const handleMenuClick = useCallback(
    (key, project) => {
      if (key === "select") handleGroupClick(project);
      else if (key === "archive") openDeleteModal(project);
      else if (key === "restore") handleRestore(project);
      else if (key === "delegate") openDelegateModal(project);
      else if (key === "share") openShareModal(project);
      else if (key === "transfer") openTransferModal(project);
    },
    [handleGroupClick, openDeleteModal, handleRestore, openDelegateModal, openShareModal, openTransferModal]
  );

  // ── Table Columns ──
  const buildColumns = useCallback(
    (source) => [
      {
        title: "#",
        dataIndex: "_index",
        key: "_index",
        width: 50,
        filterable: false,
        render: (_, __, index) => <span>{String(index + 1).padStart(2, "0")}</span>,
      },
      {
        title: "GROUP NAME",
        dataIndex: "project_name",
        key: "project_name",
        render: (text) => (
          <GroupNameCell>
            <span>{text}</span>
            <RoleBadge $variant={source}>{BADGE_LABELS[source]}</RoleBadge>
          </GroupNameCell>
        ),
      },
      {
        title: "PROJECT #",
        dataIndex: "sb_project_number",
        key: "sb_project_number",
        render: (text) => text || "—",
      },
      {
        title: "STATUS",
        dataIndex: "status",
        key: "status",
        render: (text) => {
          const colorMap = { Completed: "green", Processing: "blue", Failed: "red", Created: "default" };
          return <SbTag color={colorMap[text] || "default"}>{text || "—"}</SbTag>;
        },
      },
      {
        title: "DELEGATED",
        key: "delegated_status",
        align: "center",
        width: 105,
        // Cell shows Yes/No — boolean filter values label the same way.
        filterValue: (record) => getAccessUserCount(getProjectDelegateUsers(record)) > 0,
        render: (_, record) =>
          renderAccessStatusTag(getAccessUserCount(getProjectDelegateUsers(record)), "delegated"),
      },
      {
        title: "SHARED",
        key: "shared_status",
        align: "center",
        width: 95,
        filterValue: (record) => getAccessUserCount(getProjectSharedUsers(record)) > 0,
        render: (_, record) =>
          renderAccessStatusTag(getAccessUserCount(getProjectSharedUsers(record)), "shared"),
      },
      {
        title: "ARCHIVE STATUS",
        dataIndex: "archived_status",
        key: "archived_status",
        align: "center",
        // Blank status renders as ACTIVE inside a tag — filter on the same text.
        filterValue: (record) => record?.archived_status || "ACTIVE",
        render: (text) => {
          const isArchived = text === PAIC_ARCHIVE_STATUS.ARCHIVED;
          return <SbTag color={isArchived ? "orange" : "green"}>{text || "ACTIVE"}</SbTag>;
        },
      },
      {
        title: "ACTIONS",
        key: "actions",
        align: "center",
        width: 80,
        render: (_, record) => {
          const items = getActionMenuItems(record, source);
          return (
            <ActionsCell>
              <SbDropdown
                menu={{ items, onClick: ({ key }) => handleMenuClick(key, record) }}
                trigger={["click"]}
              >
                <ActionMenuButton type="button" onClick={(e) => e.stopPropagation()}>
                  <EllipsisOutlined />
                </ActionMenuButton>
              </SbDropdown>
            </ActionsCell>
          );
        },
      },
    ],
    [getActionMenuItems, handleMenuClick]
  );

  // ── Tab Items ──
  const tabItems = useMemo(
    () => [
      {
        key: MANAGE_GROUPS_TABS.MY_GROUPS,
        label: (
          <TabLabelWithCount>
            My Groups <TabCount>{ownedCount}</TabCount>
          </TabLabelWithCount>
        ),
        children: (
          <ManageGroupsTableWrapper>
            <SbTable
              dataSource={filteredOwnedProjects}
              columns={buildColumns(PAIC_PROJECT_SOURCE.OWNED)}
              rowKey="sb_project_unique_id"
              pagination={false}
              size="middle"
              scroll={{ x: 980, y: 360 }}
              loading={actionLoading}
            />
          </ManageGroupsTableWrapper>
        ),
      },
      {
        key: MANAGE_GROUPS_TABS.DELEGATED_TO_ME,
        label: (
          <TabLabelWithCount>
            Delegated to me <TabCount>{delegatedCount}</TabCount>
          </TabLabelWithCount>
        ),
        children: (
          <ManageGroupsTableWrapper>
            <SbTable
              dataSource={filteredDelegatedProjects}
              columns={buildColumns(PAIC_PROJECT_SOURCE.DELEGATED)}
              rowKey="sb_project_unique_id"
              pagination={false}
              size="middle"
              scroll={{ x: 980, y: 360 }}
              loading={actionLoading}
            />
          </ManageGroupsTableWrapper>
        ),
      },
      {
        key: MANAGE_GROUPS_TABS.SHARED_WITH_ME,
        label: (
          <TabLabelWithCount>
            Shared with me <TabCount>{sharedCount}</TabCount>
          </TabLabelWithCount>
        ),
        children: (
          <ManageGroupsTableWrapper>
            <SbTable
              dataSource={filteredSharedProjects}
              columns={buildColumns(PAIC_PROJECT_SOURCE.SHARED)}
              rowKey="sb_project_unique_id"
              pagination={false}
              size="middle"
              scroll={{ x: 980, y: 360 }}
              loading={actionLoading}
            />
          </ManageGroupsTableWrapper>
        ),
      },
    ],
    [filteredOwnedProjects, filteredDelegatedProjects, filteredSharedProjects, ownedCount, delegatedCount, sharedCount, buildColumns, actionLoading]
  );

  return (
    <>
      {/* ── Main Manage Groups Modal ── */}
      <SbModal
        open={open}
        title={
          <ModalTitleRow>
            <ModalTitleIcon><TeamOutlined /></ModalTitleIcon>
            Manage Groups
          </ModalTitleRow>
        }
        onCancel={() => { setModalSearchTerm(""); onClose(); }}
        footer={
          <ModalFooterRow>
            <FooterSummary>
              Total {totalCount} groups · {ownedCount} owned · {delegatedCount} delegated · {sharedCount} shared
            </FooterSummary>
            <SbButton buttonType="default" onClick={onClose}>Close</SbButton>
          </ModalFooterRow>
        }
        width={1040}
        centered
        destroyOnClose
        afterClose={() => setModalSearchTerm("")}
      >
        <ModalSearchRow>
          <SbInput
            placeholder="Search groups by name, project #, or status..."
            value={modalSearchTerm}
            onChange={(e) => setModalSearchTerm(e.target.value)}
            allowClear
            suffix={
              <ModalSearchIconBox><SearchOutlined /></ModalSearchIconBox>
            }
          />
        </ModalSearchRow>
        <SbTabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </SbModal>

      {/* ── Archive Group Modal ── */}
      <SbModal
        open={deleteModalOpen}
        title={
          <DeleteModalTitleRow>
            <DeleteModalTitleIcon><DeleteOutlined /></DeleteModalTitleIcon>
            Archive Group
          </DeleteModalTitleRow>
        }
        onCancel={closeDeleteModal}
        footer={
          <ModalActionFooter>
            <SbButton buttonType="default" onClick={closeDeleteModal}>Cancel</SbButton>
            <SbButton
              buttonType="red"
              onClick={handleDeleteConfirm}
              disabled={!isDeleteConfirmMatch || actionLoading}
            >
              {actionLoading ? "Archiving..." : "Archive Group"}
            </SbButton>
          </ModalActionFooter>
        }
        width={520}
        centered
        destroyOnClose
      >
        <div>You are about to permanently archive the group:</div>
        <DeleteGroupNameBox>{deleteProject?.project_name}</DeleteGroupNameBox>
        <DeleteWarningBox>
          <WarningFilled />
          <DeleteWarningText>
            This action <strong>cannot be undone</strong>. All agent evaluations, versions,
            and selections within this group will be permanently lost.
          </DeleteWarningText>
        </DeleteWarningBox>
        <ConfirmLabel>To confirm, type the group name exactly:</ConfirmLabel>
        <ConfirmInputWrapper>
          <SbInput
            placeholder="Type group name to confirm..."
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && isDeleteConfirmMatch) handleDeleteConfirm();
            }}
          />
        </ConfirmInputWrapper>
      </SbModal>

      {/* ── Transfer Ownership Modal ── */}
      <SbModal
        open={transferModalOpen}
        title={
          <TransferModalTitleRow>
            <TransferModalTitleIcon><SyncOutlined /></TransferModalTitleIcon>
            Transfer Ownership
          </TransferModalTitleRow>
        }
        onCancel={closeTransferModal}
        footer={
          <ModalActionFooter>
            <SbButton buttonType="default" onClick={closeTransferModal}>Cancel</SbButton>
            <SbButton
              buttonType="primary"
              onClick={handleTransferSubmit}
              disabled={isTransferSubmitDisabled}
              style={{ background: "#d48806", borderColor: "#d48806" }}
            >
              {transferLoading ? "Transferring..." : "Transfer Ownership"}
            </SbButton>
          </ModalActionFooter>
        }
        width={520}
        centered
        destroyOnClose
      >
        <TransferDescription>
          Transfer ownership of <strong>&quot;{transferProject?.project_name}&quot;</strong> to another user.
          They will become the new owner and <strong>you will lose access</strong> to this group.
        </TransferDescription>
        <TransferWarningBox>
          <WarningFilled />
          <TransferWarningText>
            This action is immediate. Once transferred, you will no longer see this
            group in your My Groups list.
          </TransferWarningText>
        </TransferWarningBox>
        <TransferFieldLabel>Transfer to (single user only)</TransferFieldLabel>
        <ConfirmInputWrapper>
          <SbInput
            placeholder="Enter email address of new owner..."
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && transferEmail.trim()) handleTransferSubmit();
            }}
          />
        </ConfirmInputWrapper>
        <TransferHelperText>Use an email from {organizationEmailHint}.</TransferHelperText>
      </SbModal>

      {/* ── Delegate Access Modal ── */}
      <SbModal
        open={delegateModalOpen}
        title={
          <DelegateModalTitleRow>
            <DelegateModalTitleIcon><TeamOutlined /></DelegateModalTitleIcon>
            Delegate Access
          </DelegateModalTitleRow>
        }
        onCancel={closeDelegateModal}
        footer={
          <ModalActionFooter>
            <SbButton buttonType="default" onClick={closeDelegateModal}>Cancel</SbButton>
            <SbButton
              buttonType="primary"
              onClick={handleDelegateSubmit}
              disabled={isDelegateSubmitDisabled}
            >
              {delegateLoading ? "Updating..." : "Update Delegate Access"}
            </SbButton>
          </ModalActionFooter>
        }
        width={520}
        centered
        destroyOnClose
      >
        <DelegateDescription>
          Grant co-creator access to <strong>"{delegateProject?.project_name}"</strong>.
          Delegates can modify agent selections and versions within this group.{" "}
          <strong>You remain the owner.</strong>
        </DelegateDescription>
        <DelegateInfoBox>
          <InfoCircleOutlined />
          <DelegateInfoText>
            Delegates can edit agent selections and create new versions but
            cannot share, transfer, or delete the group.
          </DelegateInfoText>
        </DelegateInfoBox>
        <ShareFieldStack>
          <SharedUsersPanel>
            <SharedUsersHeader>
              <SharedUsersHeaderContent>
                <TeamOutlined />
                Currently delegated to
              </SharedUsersHeaderContent>
              <SharedUsersCount>
                {existingDelegateUsers.length} {existingDelegateUsers.length === 1 ? "user" : "users"}
              </SharedUsersCount>
            </SharedUsersHeader>
            {existingDelegateUsers.length > 0 ? (
              <>
                <SharedUsersSearchWrapper>
                  <SbInput
                    placeholder="Search delegated users..."
                    value={delegateUserSearchTerm}
                    onChange={(e) => setDelegateUserSearchTerm(e.target.value)}
                    allowClear
                    suffix={<SearchOutlined />}
                  />
                </SharedUsersSearchWrapper>
                {delegateUserSearchTerm.trim() && (
                  <SharedUsersResultMeta>
                    Showing {filteredDelegateUsers.length} of {existingDelegateUsers.length} delegated users
                  </SharedUsersResultMeta>
                )}
                {filteredDelegateUsers.length > 0 ? (
                  <SharedUsersList>
                    {filteredDelegateUsers.map((user) => {
                      const email = getUserEmail(user);
                      return (
                        <SharedUserItem key={user.sb_user_unique_id || email}>
                          <SharedUserName title={getDisplayName(user)}>{getDisplayName(user)}</SharedUserName>
                          <SharedUserEmail title={email}>{email}</SharedUserEmail>
                        </SharedUserItem>
                      );
                    })}
                  </SharedUsersList>
                ) : (
                  <SharedUsersNoResultsText>
                    No delegated users match your search.
                  </SharedUsersNoResultsText>
                )}
              </>
            ) : (
              <SharedUsersEmptyText>
                This group is not currently delegated to anyone.
              </SharedUsersEmptyText>
            )}
          </SharedUsersPanel>

          <div>
            <DelegateFieldLabel>
              Add co-creators <span>· separate multiple emails with a comma or semicolon</span>
            </DelegateFieldLabel>
            <DelegateInputWrapper>
              <SbSelect
                mode={DROPDOWN_MODE.TAGS}
                value={delegateEmailsToAdd}
                searchValue={delegateAddSearchValue}
                onSearch={setDelegateAddSearchValue}
                onChange={handleDelegateEmailChange}
                onInputKeyDown={handleDelegateInputKeyDown}
                placeholder="Add email address..."
                tokenSeparators={[",", ";"]}
                options={[]}
                open={false}
              />
            </DelegateInputWrapper>
            <DelegateHelperText>Type an {organizationEmailHint} email and press Enter, comma, or semicolon to add it. Typed email is also included when you submit.</DelegateHelperText>
          </div>

          <ShareSectionDivider />

          <div>
            <DelegateFieldLabel>
              Remove co-creators <span>· optional</span>
            </DelegateFieldLabel>
            <DelegateInputWrapper>
              <SbSelect
                mode="multiple"
                value={delegateEmailsToRemove}
                onChange={handleDelegateRemoveEmailChange}
                onInputKeyDown={handleDelegateInputKeyDown}
                placeholder={existingDelegateUsers.length > 0 ? "Search and select users to remove..." : "No delegated users to remove"}
                options={existingDelegateUserOptions}
                disabled={existingDelegateUsers.length === 0}
              />
            </DelegateInputWrapper>
            <DelegateHelperText>Users removed here will lose delegated co-creator access to this group.</DelegateHelperText>
          </div>
        </ShareFieldStack>
      </SbModal>

      {/* ── Share Access Modal ── */}
      <SbModal
        open={shareModalOpen}
        title={
          <DelegateModalTitleRow>
            <DelegateModalTitleIcon><ShareAltOutlined /></DelegateModalTitleIcon>
            Share Access
          </DelegateModalTitleRow>
        }
        onCancel={closeShareModal}
        footer={
          <ModalActionFooter>
            <SbButton buttonType="default" onClick={closeShareModal}>Cancel</SbButton>
            <SbButton
              buttonType="primary"
              onClick={handleShareSubmit}
              disabled={isShareSubmitDisabled}
            >
              {shareLoading ? "Updating..." : "Update View Access"}
            </SbButton>
          </ModalActionFooter>
        }
        width={520}
        centered
        destroyOnClose
      >
        <DelegateDescription>
          Grant view-only access to <strong>&quot;{shareProject?.project_name}&quot;</strong>.
          Shared users can view agents, PRDs, and JDs but <strong>cannot edit</strong> anything.{" "}
          <strong>You remain the owner.</strong>
        </DelegateDescription>
        <DelegateInfoBox>
          <InfoCircleOutlined />
          <DelegateInfoText>
            Shared users have read-only access. They cannot modify agent selections,
            create versions, or submit changes.
          </DelegateInfoText>
        </DelegateInfoBox>
        <ShareFieldStack>
          <SharedUsersPanel>
            <SharedUsersHeader>
              <SharedUsersHeaderContent>
                <ShareAltOutlined />
                Currently shared with
              </SharedUsersHeaderContent>
              <SharedUsersCount>
                {existingSharedUsers.length} {existingSharedUsers.length === 1 ? "user" : "users"}
              </SharedUsersCount>
            </SharedUsersHeader>
            {existingSharedUsers.length > 0 ? (
              <>
                <SharedUsersSearchWrapper>
                  <SbInput
                    placeholder="Search shared users..."
                    value={shareUserSearchTerm}
                    onChange={(e) => setShareUserSearchTerm(e.target.value)}
                    allowClear
                    suffix={<SearchOutlined />}
                  />
                </SharedUsersSearchWrapper>
                {shareUserSearchTerm.trim() && (
                  <SharedUsersResultMeta>
                    Showing {filteredSharedUsers.length} of {existingSharedUsers.length} shared users
                  </SharedUsersResultMeta>
                )}
                {filteredSharedUsers.length > 0 ? (
                  <SharedUsersList>
                    {filteredSharedUsers.map((user) => {
                      const email = getUserEmail(user);
                      return (
                        <SharedUserItem key={user.sb_user_unique_id || email}>
                          <SharedUserName title={getDisplayName(user)}>{getDisplayName(user)}</SharedUserName>
                          <SharedUserEmail title={email}>{email}</SharedUserEmail>
                        </SharedUserItem>
                      );
                    })}
                  </SharedUsersList>
                ) : (
                  <SharedUsersNoResultsText>
                    No shared users match your search.
                  </SharedUsersNoResultsText>
                )}
              </>
            ) : (
              <SharedUsersEmptyText>
                This group is not currently shared with anyone.
              </SharedUsersEmptyText>
            )}
          </SharedUsersPanel>

          <div>
            <DelegateFieldLabel>
              Add users <span>· separate multiple emails with a comma or semicolon</span>
            </DelegateFieldLabel>
            <DelegateInputWrapper>
              <SbSelect
                mode={DROPDOWN_MODE.TAGS}
                value={shareEmailsToAdd}
                searchValue={shareAddSearchValue}
                onSearch={setShareAddSearchValue}
                onChange={handleShareEmailChange}
                onInputKeyDown={handleShareInputKeyDown}
                placeholder="Add email address..."
                tokenSeparators={[",", ";"]}
                options={[]}
                open={false}
              />
            </DelegateInputWrapper>
            <DelegateHelperText>Type an {organizationEmailHint} email and press Enter, comma, or semicolon to add it. Typed email is also included when you submit.</DelegateHelperText>
          </div>

          <ShareSectionDivider />

          <div>
            <DelegateFieldLabel>
              Remove users <span>· optional</span>
            </DelegateFieldLabel>
            <DelegateInputWrapper>
              <SbSelect
                mode="multiple"
                value={shareEmailsToRemove}
                onChange={handleShareRemoveEmailChange}
                onInputKeyDown={handleShareInputKeyDown}
                placeholder={existingSharedUsers.length > 0 ? "Search and select users to remove..." : "No shared users to remove"}
                options={existingSharedUserOptions}
                disabled={existingSharedUsers.length === 0}
              />
            </DelegateInputWrapper>
            <DelegateHelperText>Users removed here will lose shared view access to this group.</DelegateHelperText>
          </div>
        </ShareFieldStack>
      </SbModal>
    </>
  );
};

export default ManageGroupsModal;
